import React, { useState, useEffect, useCallback } from 'react';
import { locationAPI } from '../services/api';

const EVENT_META = {
  job_started: { label: 'Job Started', color: 'var(--accent)' },
  job_completed: { label: 'Job Completed', color: 'var(--green)' },
  visit_note: { label: "Can't Complete Note", color: 'var(--yellow)' },
};

const eventMeta = (type) =>
  EVENT_META[type] || { label: type || 'Activity', color: 'var(--text3)' };

const LiveMap = () => {
  const [engineers, setEngineers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [date, setDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadSummary = useCallback(async (targetDate) => {
    setLoading(true);
    try {
      const res = await locationAPI.getActivitySummary({
        date: targetDate || date,
      });
      setEngineers(res.data.data.engineers);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadSummary(date);
  }, [date, loadSummary]);

  const loadHistory = async (engineerId, targetDate) => {
    try {
      const res = await locationAPI.getEngineerActivity(
        engineerId, { date: targetDate || date });
      setHistory(res.data.data.events || []);
    } catch (err) {
      console.error('Failed:', err);
    }
  };

  const selectEngineer = (eng) => {
    if (selected?.id === eng.id) {
      setSelected(null);
      setHistory([]);
      return;
    }
    setSelected(eng);
    loadHistory(eng.id, date);
  };

  const engineersWithActivity = engineers.filter(
    e => parseInt(e.activity_count) > 0);

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:'24px',
        flexWrap:'wrap', gap:'12px',
      }}>
        <div>
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'22px', fontWeight:'800',
          }}>
            Field Activity
          </h1>
          <p style={{
            color:'var(--text3)',
            fontSize:'13px', marginTop:'4px',
          }}>
            Where your engineers actually did work
            {lastUpdated && (
              <span style={{
                fontFamily:'DM Mono,monospace',
                marginLeft:'10px',
              }}>
                · Last updated:{' '}
                {lastUpdated.toLocaleTimeString(
                  'en-IN',
                  { hour:'2-digit', minute:'2-digit' }
                )}
              </span>
            )}
          </p>
        </div>
        <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
          <input
            type="date"
            value={date}
            max={new Date().toISOString().split('T')[0]}
            onChange={(e) => {
              setDate(e.target.value);
              setSelected(null);
              setHistory([]);
            }}
            style={{
              background:'var(--surface2)',
              border:'1px solid var(--border2)',
              color:'var(--text)',
              borderRadius:'8px',
              padding:'8px 12px',
              fontSize:'13px',
              fontFamily:'DM Mono,monospace',
              colorScheme:'dark',
            }}
          />
          <button
            onClick={() => loadSummary(date)}
            style={{
              background:'var(--surface2)',
              color:'var(--text2)',
              border:'1px solid var(--border2)',
              padding:'8px 16px',
              borderRadius:'8px',
              fontSize:'13px', cursor:'pointer',
            }}
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Map + Engineers */}
      <div style={{
        display:'grid',
        gridTemplateColumns:'1fr 320px',
        gap:'16px',
      }}>
        {/* Map Area */}
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          overflow:'hidden',
        }}>
          <div style={{
            padding:'16px 20px',
            borderBottom:'1px solid var(--border)',
            fontFamily:'Syne,sans-serif',
            fontSize:'14px', fontWeight:'700',
          }}>
            Activity Locations
          </div>

          {/* Map Visualization */}
          <div style={{
            height:'400px',
            background:`
              radial-gradient(ellipse at 30% 40%,
                rgba(0,212,255,0.06) 0%,
                transparent 50%),
              radial-gradient(ellipse at 70% 70%,
                rgba(159,122,234,0.05) 0%,
                transparent 40%),
              repeating-linear-gradient(0deg,
                transparent, transparent 39px,
                rgba(255,255,255,0.03) 39px,
                rgba(255,255,255,0.03) 40px),
              repeating-linear-gradient(90deg,
                transparent, transparent 39px,
                rgba(255,255,255,0.03) 39px,
                rgba(255,255,255,0.03) 40px)
            `,
            position:'relative',
            display:'flex',
            alignItems:'center',
            justifyContent:'center',
          }}>
            {loading ? (
              <div style={{
                color:'var(--text3)',
                fontFamily:'DM Mono,monospace',
                fontSize:'13px',
              }}>
                Loading activity...
              </div>
            ) : engineersWithActivity.length === 0 ? (
              <div style={{
                textAlign:'center',
                color:'var(--text3)',
              }}>
                <div style={{
                  fontSize:'40px',
                  marginBottom:'12px',
                }}>
                  ◎
                </div>
                <div style={{
                  fontSize:'14px',
                  color:'var(--text2)',
                }}>
                  No activity on this day
                </div>
                <div style={{
                  fontSize:'12px',
                  marginTop:'6px',
                }}>
                  Dots appear here once an engineer
                  starts, completes, or logs a note
                  on a job
                </div>
              </div>
            ) : (
              <>
                {/* Activity Dots */}
                {engineersWithActivity.map((eng, i) => {
                  const positions = [
                    { left:'25%', top:'35%' },
                    { left:'55%', top:'25%' },
                    { left:'70%', top:'55%' },
                    { left:'40%', top:'65%' },
                    { left:'80%', top:'30%' },
                    { left:'15%', top:'60%' },
                  ];
                  const pos = positions[i % positions.length];
                  const color = eventMeta(eng.last_event_type).color;
                  return (
                    <div key={eng.id}
                      onClick={() => selectEngineer(eng)}
                      style={{
                        position:'absolute',
                        left: eng.longitude
                          ? `${((parseFloat(eng.longitude) - 68) / (97 - 68)) * 100}%`
                          : pos.left,
                        top: eng.latitude
                          ? `${100 - ((parseFloat(eng.latitude) - 8) / (37 - 8)) * 100}%`
                          : pos.top,
                        width:'14px', height:'14px',
                        borderRadius:'50%',
                        background: color,
                        boxShadow: `0 0 0 3px ${color}44, 0 0 12px ${color}66`,
                        cursor:'pointer',
                        zIndex:2,
                        transition:'all 0.3s',
                        transform:
                          selected?.id === eng.id
                            ? 'scale(1.5)' : 'scale(1)',
                      }}
                      title={`${eng.name} \u2014 ${eventMeta(eng.last_event_type).label}`}
                    />
                  );
                })}

                {/* Legend */}
                <div style={{
                  position:'absolute',
                  top:'12px', right:'12px',
                  background:'rgba(17,22,32,0.9)',
                  border:'1px solid var(--border)',
                  borderRadius:'8px',
                  padding:'10px 14px',
                  display:'flex',
                  flexDirection:'column',
                  gap:'6px',
                }}>
                  {Object.values(EVENT_META).map(m => (
                    <div key={m.label} style={{
                      display:'flex',
                      alignItems:'center',
                      gap:'7px', fontSize:'11px',
                      color:'var(--text2)',
                      fontFamily:'DM Mono,monospace',
                    }}>
                      <div style={{
                        width:'8px', height:'8px',
                        borderRadius:'50%',
                        background: m.color,
                      }} />
                      {m.label}
                    </div>
                  ))}
                </div>

                <div style={{
                  position:'absolute',
                  bottom:'12px', left:'16px',
                  fontSize:'10px',
                  fontFamily:'DM Mono,monospace',
                  color:'var(--text3)',
                }}>
                  Click a dot (or an engineer in the
                  list) to see their full activity trail
                </div>
              </>
            )}
          </div>

          {/* Info Note */}
          <div style={{
            padding:'12px 20px',
            background:'rgba(0,212,255,0.05)',
            borderTop:'1px solid var(--border)',
            fontSize:'12px',
            color:'var(--text3)',
          }}>
            📍 These locations come from real job activity
            \u2014 when an engineer starts a job, completes
            a job with proof and signature, or logs a
            can't-complete note. This is not a continuous
            live feed.
          </div>
        </div>

        {/* Engineer List + Detail */}
        <div style={{
          display:'flex',
          flexDirection:'column', gap:'10px',
        }}>
          <div style={{
            background:'var(--surface)',
            border:'1px solid var(--border)',
            borderRadius:'12px',
            overflow:'hidden',
          }}>
            <div style={{
              padding:'14px 16px',
              borderBottom:'1px solid var(--border)',
              fontFamily:'Syne,sans-serif',
              fontSize:'13px', fontWeight:'700',
            }}>
              All Engineers
            </div>
            <div>
              {loading ? (
                <div style={{
                  padding:'20px',
                  textAlign:'center',
                  color:'var(--text3)',
                  fontSize:'12px',
                }}>
                  Loading...
                </div>
              ) : engineers.length === 0 ? (
                <div style={{
                  padding:'20px',
                  textAlign:'center',
                  color:'var(--text3)',
                  fontSize:'12px',
                }}>
                  No engineers found
                </div>
              ) : (
                engineers.map(eng => {
                  const hasActivity = parseInt(eng.activity_count) > 0;
                  const meta = eventMeta(eng.last_event_type);
                  return (
                    <div key={eng.id}
                      onClick={() => selectEngineer(eng)}
                      style={{
                        padding:'12px 16px',
                        borderBottom:'1px solid var(--border)',
                        cursor:'pointer',
                        background:
                          selected?.id === eng.id
                            ? 'rgba(0,212,255,0.05)'
                            : 'transparent',
                        transition:'all 0.15s',
                      }}
                    >
                      <div style={{
                        display:'flex',
                        justifyContent:'space-between',
                        alignItems:'center',
                        marginBottom:'4px',
                      }}>
                        <div style={{
                          fontWeight:'600',
                          fontSize:'13px',
                        }}>
                          {eng.name}
                        </div>
                        {hasActivity && (
                          <div style={{
                            fontSize:'10px',
                            fontFamily:'DM Mono,monospace',
                            color:'var(--text3)',
                          }}>
                            {eng.activity_count}{' '}
                            {parseInt(eng.activity_count) === 1
                              ? 'event' : 'events'}
                          </div>
                        )}
                      </div>
                      {hasActivity ? (
                        <>
                          <div style={{
                            display:'flex',
                            alignItems:'center',
                            gap:'5px',
                            fontSize:'11px',
                            color: meta.color,
                            fontFamily:'DM Mono,monospace',
                          }}>
                            <div style={{
                              width:'6px', height:'6px',
                              borderRadius:'50%',
                              background: meta.color,
                            }} />
                            {meta.label}
                            {eng.last_ticket_number
                              ? ` \u2014 ${eng.last_ticket_number}` : ''}
                          </div>
                          {eng.last_activity_at && (
                            <div style={{
                              fontSize:'10px',
                              color:'var(--text3)',
                              marginTop:'3px',
                            }}>
                              Last:{' '}
                              {new Date(eng.last_activity_at)
                                .toLocaleTimeString('en-IN',
                                  { hour:'2-digit', minute:'2-digit' })}
                            </div>
                          )}
                          {eng.latitude && (
                            <a
                              href={`https://www.google.com/maps?q=${eng.latitude},${eng.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                display:'block',
                                fontSize:'10px',
                                color:'var(--accent)',
                                fontFamily:'DM Mono,monospace',
                                marginTop:'3px',
                                textDecoration:'underline',
                                textUnderlineOffset:'2px',
                              }}
                            >
                              📍{' '}
                              {parseFloat(eng.latitude).toFixed(4)}°N,{' '}
                              {parseFloat(eng.longitude).toFixed(4)}°E
                              {' ↗'}
                            </a>
                          )}
                        </>
                      ) : (
                        <div style={{
                          fontSize:'11px',
                          color:'var(--text3)',
                          fontStyle:'italic',
                        }}>
                          No activity on this day
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Activity Trail */}
          {selected && (
            <div style={{
              background:'var(--surface)',
              border:'1px solid var(--border)',
              borderRadius:'12px',
              overflow:'hidden',
            }}>
              <div style={{
                padding:'14px 16px',
                borderBottom:'1px solid var(--border)',
                fontFamily:'Syne,sans-serif',
                fontSize:'13px', fontWeight:'700',
              }}>
                Activity \u2014 {selected.name}
              </div>
              <div style={{ padding:'12px 16px' }}>
                {history.length === 0 ? (
                  <div style={{
                    fontSize:'12px',
                    color:'var(--text3)',
                    textAlign:'center',
                    padding:'12px 0',
                  }}>
                    No activity on this day
                  </div>
                ) : (
                  history.map((h, i) => {
                    const meta = eventMeta(h.event_type);
                    return (
                      <div key={i} style={{
                        display:'flex',
                        gap:'10px',
                        paddingBottom:'12px',
                        position:'relative',
                      }}>
                        {i < history.length - 1 && (
                          <div style={{
                            position:'absolute',
                            left:'5px', top:'14px',
                            bottom:0, width:'1px',
                            background:'var(--border)',
                          }} />
                        )}
                        <div style={{
                          width:'10px', height:'10px',
                          borderRadius:'50%',
                          background: meta.color,
                          flexShrink:0, marginTop:'3px',
                          zIndex:1,
                        }} />
                        <div>
                          <div style={{
                            fontSize:'11px',
                            fontWeight:'600',
                            color: meta.color,
                          }}>
                            {meta.label}
                            {h.ticket_number
                              ? ` \u2014 ${h.ticket_number}` : ''}
                          </div>
                          {h.note && (
                            <div style={{
                              fontSize:'11px',
                              color:'var(--text2)',
                              marginTop:'2px',
                              fontStyle:'italic',
                            }}>
                              "{h.note}"
                            </div>
                          )}
                          {h.latitude && (
                            <a
                              href={`https://www.google.com/maps?q=${h.latitude},${h.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display:'block',
                                fontSize:'11px',
                                fontFamily:'DM Mono,monospace',
                                color:'var(--accent)',
                                textDecoration:'underline',
                                textUnderlineOffset:'2px',
                                marginTop:'2px',
                              }}
                            >
                              {parseFloat(h.latitude).toFixed(4)}°N,{' '}
                              {parseFloat(h.longitude).toFixed(4)}°E
                              {' ↗'}
                            </a>
                          )}
                          <div style={{
                            fontSize:'10px',
                            color:'var(--text3)',
                            marginTop:'2px',
                          }}>
                            {new Date(h.event_time)
                              .toLocaleTimeString('en-IN',
                                { hour:'2-digit', minute:'2-digit' })}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveMap;
